import { createSelector } from 'reselect';
import {
  ZoomingParams, ViewTransform, initialViewScale,
  isZoomingEnabledSheet,
  SheetLayout,
  FitStyle,
} from '../utils/view';
import { PagesState, isPopupPanelID } from '../reducers/pages';
import { Publication } from '../types/epub';
import { SingleViewState, ViewState, getOrCreateTargetViewState } from '../reducers/view';
import { getSheetLayoutData } from '../helpers/viewGlue';
import { Rectangle } from 'ark-common';
import { Dimension } from '../types/common';
import { maxZoomRate } from '../constants';

export interface ViewTransformSelectorProps {
  panelID: string;
}

export interface ViewTransformSelectorResult {
  transform: ViewTransform;
  arrangementInfo: ZoomingParams;
}

export interface ZoomingState {
  publication?: Publication;
  pages: PagesState;
  view: ViewState;
}

const getPublication = (state: ZoomingState): Publication => {
  if (state.publication === undefined) {
    throw new Error('Unexpected state.');
  }
  return state.publication;
};

const getPagesState = (state: ZoomingState): PagesState => state.pages;

const getContainerRect = (state: ZoomingState): Rectangle => state.view.tabPanelAreaRect;

const getPopupRects = (state: ZoomingState): { [id: string]: Rectangle } => state.view.popupRects;

const getViewState = (state: ZoomingState): ViewState => state.view;

const getPanelID = (_state: ZoomingState, props: ViewTransformSelectorProps) => props.panelID;

const isZoomingEnabled = createSelector(
  [getPublication, getPagesState, getPanelID],
  (
    publication: Publication,
    pages: PagesState,
    panelID: string,
  ): boolean => {
    const layoutData = getSheetLayoutData(publication, pages, undefined, panelID);
    return isZoomingEnabledSheet(layoutData);
  });

// "false" means "not zoomable"
const getArrangementInfo = createSelector(
  [getPublication, getPagesState, getViewState, getContainerRect, getPopupRects, getPanelID],
  (
    publication: Publication,
    pages: PagesState,
    view: ViewState,
    container: Rectangle,
    popupRects: { [id: string]: Rectangle },
    panelID: string,
  ): ZoomingParams => {
    const frameSize: Dimension | undefined = view.frameContentSizes[panelID];
    const layoutData = getSheetLayoutData(publication, pages, frameSize, panelID);
    if (!isZoomingEnabledSheet(layoutData)) {
      throw new Error('getArrangementInfo must not be called for zooming disabled sheet.');
    }

    let { width: containerWidth, height: containerHeight } = container;
    if (isPopupPanelID(panelID)) {
      containerWidth = popupRects[panelID].width;
      containerHeight = popupRects[panelID].height;
    }
    return { ...layoutData, containerWidth, containerHeight };
  });

const getTargetViewState = createSelector(
  [getViewState, getPanelID, getArrangementInfo],
  (
    view: ViewState,
    panelID: string,
    params: ZoomingParams,
  ): SingleViewState => {
    return getOrCreateTargetViewState(view, panelID, params);
  });

export const viewTransformSelector = createSelector(
  [getArrangementInfo, getTargetViewState],
  (
    arrangementInfo: ZoomingParams,
    view: SingleViewState,
  ): ViewTransformSelectorResult => {
    return {
      arrangementInfo,
      transform: {
        scale: view.zoom,
        translateX: view.panX,
        translateY: view.panY,
      },
    };
  });

export const mapStateToViewTransform = (
  state: ZoomingState,
  ownProps: ViewTransformSelectorProps,
) => viewTransformSelector(state, ownProps);

export const mapStateToZoomable = (state: ZoomingState, ownProps: ViewTransformSelectorProps) => {
  if (!isZoomingEnabled(state, ownProps)) {
    return { zoomingEnabled: false, zoomInEnabled: false, zoomOutEnabled: false };
  }

  const arrangementInfo = getArrangementInfo(state, ownProps);
  const view = getTargetViewState(state, ownProps);
  const minimumScale = initialViewScale(arrangementInfo);
  return {
    zoomingEnabled: true,
    zoomInEnabled: view.zoom < minimumScale * maxZoomRate,
    zoomOutEnabled: view.zoom > minimumScale,
  };
};

export function getScale(state: ZoomingState, ownProps: ViewTransformSelectorProps) {
  if (!isZoomingEnabled(state, ownProps)) {
    return 1;
  }
  return getTargetViewState(state, ownProps).zoom;
}

export function getInitialScale(state: ZoomingState, ownProps: ViewTransformSelectorProps) {
  if (!isZoomingEnabled(state, ownProps)) {
    return 1;
  }
  const arrangementInfo = getArrangementInfo(state, ownProps);
  return initialViewScale(arrangementInfo);
}

function getSheetSize(arrangementInfo: ZoomingParams): Dimension {
  if (arrangementInfo.layout === SheetLayout.Fixed) {
    return { width: arrangementInfo.sheetWidth, height: arrangementInfo.sheetHeight };
  }
  if (arrangementInfo.fitStyle === FitStyle.FitWidth) {
    return { width: arrangementInfo.containerWidth, height: arrangementInfo.blockFlowLength };
  }
  return { width: arrangementInfo.blockFlowLength, height: arrangementInfo.containerHeight };
}

export function getVisibleRectAndScale(
  state: ZoomingState,
  ownProps: ViewTransformSelectorProps,
): [Rectangle, number] {
  const arrangementInfo = getArrangementInfo(state, ownProps);
  const sheetSize = getSheetSize(arrangementInfo);
  const view = getTargetViewState(state, ownProps);
  const scale = view.zoom;

  const { containerWidth, containerHeight } = arrangementInfo;

  const x = Math.max(0, -view.panX);
  const y = Math.max(0, -view.panY);
  const width = Math.min(sheetSize.width * scale, containerWidth - view.panX) - x;
  const height = Math.min(sheetSize.height * scale, containerHeight - view.panY) - y;

  if (scale === 0) {
    if (x !== 0 || y !== 0 || width !== 0 || height !== 0) {
      throw new Error('Unexpected: scale is 0.'); // scaleが0なのは初期状態以外あり得ない
    }
  }
  return [{ x, y, width, height }, scale];
}
